/* Round 27 · X6 — tenant screens show the plain worksheet name. Batch 15 · N3 (F-117, owner 11-Oct-2026): the internal
   code is no longer shown at all — "W10 signed" → "Audit summary memo signed", "Audit summary memo (W10)" → "Audit summary
   memo", "A9001 scan" → "Journal entry testing scan". Firm form numbers (105(a), 203(a) …) are not codes and stay. Ids
   such as "W9-tcwg-communicated", routes and test ids are untouched (no space in them, or a word / dash around the code). */
export const WS_NAMES = {
  en: { W5: "Completeness worksheet", W6: "Inventory count worksheet", W7: "Opening balances", W8: "Estimates worksheet", W9: "Misstatement worksheet",
    W10: "Audit summary memo", W11: "Clearance gate", W12: "File assembly", W13: "Audit trail", W16: "Related-party worksheet",
    W17: "Review engagement forms", "W17.1": "Acceptance, preconditions and terms", "W17.2": "Understanding the entity", "W17.3": "Inquiry and analytical procedures",
    "W17.4": "Required inquiries", "W17.5": "Specific circumstances", "W17.6": "Reconciliation to accounting records", "W17.7": "Additional procedures register", "W17.8": "Evaluation and conclusion",
    A9001: "Journal entry testing", A9002: "Tests of controls", A9003: "Opening balances" },
  ar: { W5: "ورقة الاكتمال", W6: "ورقة جرد المخزون", W7: "الأرصدة الافتتاحية", W8: "ورقة التقديرات", W9: "ورقة التحريفات",
    W10: "مذكرة ملخص المراجعة", W11: "بوابة الإخلاء", W12: "تجميع الملف", W13: "سجل التدقيق", W16: "ورقة الأطراف ذات العلاقة",
    W17: "نماذج ارتباط الفحص", "W17.1": "القبول والشروط المسبقة والأحكام", "W17.2": "فهم المنشأة", "W17.3": "الاستفسار والإجراءات التحليلية",
    "W17.4": "الاستفسارات المطلوبة", "W17.5": "الظروف الخاصة", "W17.6": "المطابقة مع السجلات المحاسبية", "W17.7": "سجل الإجراءات الإضافية", "W17.8": "التقييم والخلاصة",
    A9001: "اختبار قيود اليومية", A9002: "اختبارات الضوابط", A9003: "الأرصدة الافتتاحية" },
};

// a code on its own: not glued to a word, a dash, a slash or a dot (ids / refs); bidi isolates around it are allowed
const CODE_SRC = "(W(?:17\\.[1-8]|1[0-7]|[5-9])|A900[1-3])";
const BARE = new RegExp(`(^|[^\\w\\-\\/.])${CODE_SRC}(?![\\w\\-\\/]|\\.\\w)`, "g");   // "(W10 open)" → "(Audit summary memo open)"; a sentence-ending "W9." is still a code
const ISOLATED = new RegExp(`\\u2066${CODE_SRC}\\u2069`, "g");                       // "⁦W9⁩" in the Arabic strings
const BRACKETED = new RegExp(`\\s*\\(${CODE_SRC}\\)`, "g");                           // "Audit summary memo (W10)"

// Batch 16 · Q10: a stored reference with a dash suffix ("A9001-03", "W17.7-001") reads "Journal entry testing 03" /
// "Additional procedures register 001" on screens, Help and export headings; the stored value itself is unchanged.
const REF_SRC = `${CODE_SRC}-(\\d{2,3})`;
const REF_EXACT = new RegExp(`^${REF_SRC}$`);
const REF_INTEXT = new RegExp(`(^|[^\\w\\-\\/.])${REF_SRC}(?![\\w\\-])`, "g");

/** "A9001-03" → "Journal entry testing 03" (unknown codes and other strings come back unchanged). */
export function refLabel(ref, lang = "en") {
  if (typeof ref !== "string") return ref;
  const m = REF_EXACT.exec(ref.trim());
  if (!m) return ref;
  const names = WS_NAMES[lang] || WS_NAMES.en;
  return names[m[1]] ? `${names[m[1]]} ${m[2]}` : ref;
}

/** Every bare or bracketed worksheet code becomes its plain name (the code itself disappears). */
export function nameCodes(s, lang = "en") {
  if (typeof s !== "string" || !/[WA]\d/.test(s)) return s;
  const names = WS_NAMES[lang] || WS_NAMES.en;
  let out = s.replace(REF_INTEXT, (m, pre, code, num) => (names[code] ? `${pre}${names[code]} ${num}` : m));
  out = out.replace(BRACKETED, (m, code) => (names[code] ? "" : m));
  out = out.replace(ISOLATED, (m, code) => (names[code] ? names[code] : m));
  out = out.replace(BARE, (m, pre, code) => (names[code] ? `${pre}${names[code]}` : m));
  return out;
}

/** The plain name of a code (the code itself when unknown). */
export function wsLabel(code, lang = "en") {
  const names = WS_NAMES[lang] || WS_NAMES.en;
  return names[code] || code;
}

/** The scan used by the tests and the crawl: any visible code left (dash forms such as "W17-3" included). */
export const CODE_SCAN = /(?<![A-Za-z_\u0600-\u06FF])(W(?:17[.\-][1-8]|1[0-7]|9)|A900[1-3])(?![\d\-])/;
/** Batch 16 · Q10: a stored reference shown as a code ("A9001-03", "W17.7-001", "W17-3-001"). */
export const REF_SCAN = /(?<![A-Za-z_\u0600-\u06FF])(W(?:17[.\-][1-8]|1[0-7]|[5-9])|A900[1-3])-\d{2,3}(?![\w\-])/;
